import { types as AccountTypes } from 'js/actions/account-actions';
import { createSelector } from 'reselect';
import _ from 'lodash';
import { denormalize } from 'normalizr';
import { SCHEMAS } from 'js/api';

export const QueryBase = Object.freeze({
  AccountSummary: 'account-summary',
  UsersInAccount: 'users-in-account',
  UsersInWorkspace: 'users-in-workspace',
  AssessmentInvites: 'assessment-invites',
  AssessmentTemplates: 'assessment-templates',
  WorkspaceOrgs: 'workspace-orgs',
  TeamScores: 'team-scores',
  QuestionScores: 'question-scores',
  TeamParticipation: 'team-participation',
  AssessmentHistorySummaries: 'assessment-history-summaries',
  Connectors: 'connectors',
  RubricTeamScores: 'rubric-team-scores',
  OrgRubricScores: 'org-rubric-scores',
  OrgTrends: 'org-trends',
  OrgSnapshotSets: 'org-snapshot-sets',
  OrgSnapshotSet: 'org-snapshot-set',
  OrgSnapshot: 'org-snapshot',
  OrgSnapshotScores: 'org-snapshot-scores',
  OrgSnapshotCompare: 'org-snapshot-compare',
});

/** Same cache key as org detail (`OrgContainer`). */
export function assessmentHistorySummariesQueryId(orgId) {
  return `${QueryBase.AssessmentHistorySummaries}-${orgId}`;
}

const initialState = {};

const queries = (state = initialState, action) => {
  switch (action.type) {
    case AccountTypes.LOGOUT:
    case AccountTypes.LOGOUT_SUCCESS:
    case 'API_ERROR/INVALID_TOKEN':
      return initialState;

    default:
    // noop
  }

  if (action.meta && action.meta.queryId) {
    const previous = state[action.meta.queryId];
    const nextResult =
      action.data && Object.prototype.hasOwnProperty.call(action.data, 'result')
        ? action.data.result
        : previous?.result ?? null;

    return {
      ...state,
      [action.meta.queryId]: {
        id: action.meta.queryId,
        ...action.status,
        result: nextResult,
        error: action.error ? action.error : null,
      },
    };
  }

  return state;
};

const selectEntities = (store) => store.entities;
const selectQuery =
  (queryBase) =>
  (store, id, ..._args) => {
    return store.queries[`${queryBase}-${id}`];
  };

export const selectWorkspaceUsersQuery = createSelector(
  selectQuery(QueryBase.UsersInWorkspace),
  selectEntities,
  (query, entities) => {
    if (!query) {
      return { query: {}, data: [] };
    }

    const data = query?.result
      ? _.chain(
          denormalize(query.result, SCHEMAS.WORKSPACE_USER_ARRAY, entities)
        )
          .filter((u) => !u.is_guest)
          .sortBy(['username'])
          .value()
      : [];

    return { query, data };
  }
);

export const selectAccountUsersQuery = createSelector(
  selectQuery(QueryBase.UsersInAccount),
  selectEntities,
  (query, entities) => {
    if (!query) {
      return { query: {}, data: [] };
    }

    const data = query?.result
      ? denormalize(query.result, SCHEMAS.ACCOUNT_USER_ARRAY, entities)
      : [];

    return { query, data };
  }
);

export const selectAssessmentInvitesQuery = createSelector(
  selectQuery(QueryBase.AssessmentInvites),
  selectEntities,
  (query, entities) => {
    if (!query) {
      return { query: {}, data: [] };
    }

    const data = query?.result
      ? _.chain(
          denormalize(query.result, SCHEMAS.ASSESSMENT_INVITES_ARRAY, entities)
        )
          .sortBy(['email'])
          .value()
      : [];

    return { query, data };
  }
);

export const selectAssessmentTemplatesQuery = createSelector(
  selectQuery(QueryBase.AssessmentTemplates),
  selectEntities,
  (query, entities) => {
    if (!query) {
      return { query: {}, data: [] };
    }

    const data = query?.result
      ? denormalize(query.result, SCHEMAS.ASSESSMENT_TEMPLATE_ARRAY, entities)
      : [];

    return { query, data };
  }
);

/** Same cache key as `RubricsListContainer` / org manage rubric picker. */
export function workspaceRubricsQueryId(workspaceId) {
  return `workspace-rubrics/${workspaceId}`;
}

export const selectWorkspaceRubricsQuery = createSelector(
  (store, workspaceId) => store.queries[workspaceRubricsQueryId(workspaceId)],
  selectEntities,
  (query, entities) => {
    if (!query) {
      return { query: {}, data: [] };
    }

    const data = query?.result
      ? _.chain(denormalize(query.result, SCHEMAS.RUBRIC_ARRAY, entities))
          .compact()
          .value()
      : [];

    return { query, data };
  }
);

export const selectWorkspaceOrgsQuery = createSelector(
  selectQuery(QueryBase.WorkspaceOrgs),
  (query) => {
    if (!query) {
      return { query: {}, data: [] };
    }

    const data = query?.result
      ? _.chain(query.result).sortBy(['name']).value()
      : [];

    return { query, data };
  }
);

export const selectTeamScoresQuery = createSelector(
  selectQuery(QueryBase.TeamScores),
  (query) => {
    return { query: query ?? {}, data: query?.result ?? null };
  }
);

export function unwrapQuestionScoresResult(result) {
  if (result == null) {
    return { teams: null, catalog: null };
  }

  if (Array.isArray(result)) {
    return { teams: result, catalog: null };
  }

  if (Array.isArray(result.teams)) {
    return {
      teams: result.teams,
      catalog: result.catalog ?? null,
    };
  }

  return { teams: null, catalog: result.catalog ?? null };
}

export const selectQuestionScoresQuery = createSelector(
  selectQuery(QueryBase.QuestionScores),
  (query) => {
    const { teams, catalog } = unwrapQuestionScoresResult(query?.result);
    return { query: query ?? {}, data: teams ?? null, catalog };
  }
);

export const selectOrgRubricScoresQuery = createSelector(
  selectQuery(QueryBase.OrgRubricScores),
  (query) => {
    return { query: query ?? {}, data: query?.result ?? null };
  }
);

export const selectOrgTrendsQuery = createSelector(
  selectQuery(QueryBase.OrgTrends),
  (query) => {
    return { query: query ?? {}, data: query?.result ?? null };
  }
);

export const selectTeamParticipationQuery = createSelector(
  selectQuery(QueryBase.TeamParticipation),
  (query) => {
    return { query: query ?? {}, data: query?.result ?? null };
  }
);

export const selectAssessmentHistorySummaries = createSelector(
  selectQuery(QueryBase.AssessmentHistorySummaries),
  (query) => {
    return { query: query ?? {}, data: query?.result ?? null };
  }
);

export function orgSnapshotSetQueryId(
  orgId,
  sourceType,
  sourceKey,
  archived = false
) {
  return `${QueryBase.OrgSnapshotSet}-${orgId}-${sourceType}-${sourceKey}-${
    archived ? 'archived' : 'active'
  }`;
}

export function selectOrgSnapshotSetQuery(
  store,
  orgId,
  sourceType,
  sourceKey,
  archived = false
) {
  const query =
    store.queries[
      orgSnapshotSetQueryId(orgId, sourceType, sourceKey, archived)
    ];
  return { query: query ?? {}, data: query?.result ?? null };
}

export function orgSnapshotCompareQueryId(orgId, sourceType, sourceKey) {
  return `${QueryBase.OrgSnapshotCompare}-${orgId}-${sourceType}-${sourceKey}`;
}

export function selectOrgSnapshotCompareQuery(
  store,
  orgId,
  sourceType,
  sourceKey
) {
  const query =
    store.queries[orgSnapshotCompareQueryId(orgId, sourceType, sourceKey)];
  return { query: query ?? {}, data: query?.result ?? null };
}

export const selectAccountSummary = createSelector(
  selectQuery(QueryBase.AccountSummary),
  selectEntities,
  (query) => {
    return { query: query ?? {}, data: query?.result ?? null };
  }
);

export default queries;
